import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { CreateProject, Project, UpdateProject } from '@dcc-erp/shared';

import { api, apiJson } from '../api';

const key = (companyId: string | undefined) => ['projects', companyId] as const;

export function useProjects(companyId: string | undefined, activeOnly = true) {
  return useQuery({
    queryKey: [...key(companyId), { activeOnly }],
    queryFn: () => api<Project[]>(`/companies/${companyId}/projects?activeOnly=${activeOnly}`),
    enabled: !!companyId,
  });
}

export function useProject(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['projects', companyId, id],
    queryFn: () => api<Project>(`/companies/${companyId}/projects/${id}`),
    enabled: !!companyId && !!id,
  });
}

export function useCreateProject(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<CreateProject, 'companyId'>) =>
      apiJson<typeof input, Project>('POST', `/companies/${companyId}/projects`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}

export function useUpdateProject(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProject }) =>
      apiJson<UpdateProject, Project>('PATCH', `/companies/${companyId}/projects/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}

export function useDeleteProject(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, Project>('DELETE', `/companies/${companyId}/projects/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}
